import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiCheckCircle, FiFileText, FiCalendar, FiCheckSquare } = FiIcons;

export default function QuickAddModal({ isOpen, onClose }) {
  const [step, setStep] = useState('select'); // select, form, success
  const [type, setType] = useState('');

  const handleSelect = (selectedType) => {
    setType(selectedType);
    setStep('form');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStep('success');
    setTimeout(() => {
      onClose();
      // Reset after closing
      setTimeout(() => setStep('select'), 300);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-slate-100">
            <h3 className="text-xl font-bold text-[#1a365d]">
              {step === 'select' ? 'What to add?' : 
               step === 'success' ? 'Added!' : `New ${type}`}
            </h3>
            <button 
              onClick={onClose}
              className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} />
            </button>
          </div>

          <div className="p-6">
            <AnimatePresence mode="wait">
              
              {/* Step 1: Select Type */}
              {step === 'select' && (
                <motion.div 
                  key="select"
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
                  className="grid grid-cols-1 gap-3"
                >
                  {[
                    { id: 'Event', icon: FiCalendar, desc: 'Add to family calendar', color: 'text-blue-500', bg: 'bg-blue-50' },
                    { id: 'Task', icon: FiCheckSquare, desc: 'Add to a checklist', color: 'text-green-500', bg: 'bg-green-50' },
                    { id: 'Note', icon: FiFileText, desc: 'Pin to the fridge', color: 'text-yellow-600', bg: 'bg-yellow-50' },
                  ].map(item => (
                    <button 
                      key={item.id}
                      onClick={() => handleSelect(item.id)}
                      className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-[#1a365d] hover:shadow-md transition-all text-left"
                    >
                      <div className={`w-12 h-12 rounded-xl ${item.bg} ${item.color} flex items-center justify-center text-xl`}>
                        <SafeIcon icon={item.icon} />
                      </div>
                      <div>
                        <div className="font-bold text-slate-800">{item.id}</div>
                        <div className="text-sm text-slate-500">{item.desc}</div>
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}

              {/* Step 2: Form (Mock) */}
              {step === 'form' && (
                <motion.form 
                  key="form"
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                    <input 
                      type="text" 
                      autoFocus
                      required
                      placeholder={`e.g. ${type === 'Event' ? 'Soccer Game' : type === 'Task' ? 'Buy Milk' : 'Remember to...'}`}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#1a365d] focus:border-transparent transition-all"
                    />
                  </div>
                  {type === 'Event' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Date</label>
                        <input type="date" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#1a365d]" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Time</label>
                        <input type="time" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#1a365d]" />
                      </div>
                    </div>
                  )}
                  {type === 'Note' && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Color</label>
                      <div className="flex gap-2">
                        {['bg-yellow-200', 'bg-blue-200', 'bg-pink-200', 'bg-green-200'].map(c => (
                          <div key={c} className={`w-8 h-8 rounded-full ${c} cursor-pointer border-2 border-transparent hover:border-slate-400`}></div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="pt-4 flex gap-3">
                    <button type="button" onClick={() => setStep('select')} className="flex-1 py-3 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors">
                      Back
                    </button>
                    <button type="submit" className="flex-1 py-3 rounded-xl font-bold text-[#1a365d] bg-yellow-300 hover:bg-yellow-400 transition-colors shadow-sm">
                      Save {type}
                    </button>
                  </div>
                </motion.form>
              )}

              {/* Step 3: Success */}
              {step === 'success' && (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-8 text-center"
                >
                  <motion.div 
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 10, delay: 0.1 }}
                    className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center text-3xl mb-4"
                  >
                    <SafeIcon icon={FiCheckCircle} />
                  </motion.div>
                  <h4 className="text-xl font-bold text-slate-800">Success!</h4>
                  <p className="text-slate-500 mt-1">Your {type.toLowerCase()} has been added to the board.</p>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}